import { useState, useCallback } from 'react';
import apiService from '../services/api';

/**
 * 视频信息提取相关的自定义Hook
 */
export const useVideoInfo = () => {
  const [videoInfo, setVideoInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 提取视频信息
   */
  const extractVideoInfo = useCallback(async (url, cookies = {}, netscapeCookies = '') => {
    if (!url || !url.trim()) {
      setError('请输入有效的视频URL');
      return { success: false, error: '请输入有效的视频URL' };
    }

    setIsLoading(true);
    setError(null);
    setVideoInfo(null);

    try {
      // 验证URL格式
      new URL(url);
      
      const result = await apiService.extractVideoInfo(url, cookies, netscapeCookies);
      
      if (result.success) {
        const info = {
          id: result.data.id || Date.now().toString(),
          title: result.data.title || '未知标题',
          author: result.data.uploader || result.data.author || '未知作者',
          duration: result.data.duration || 0,
          thumbnail: result.data.thumbnail || '',
          description: result.data.description || '',
          viewCount: result.data.view_count || 0,
          uploadDate: result.data.upload_date || '',
          formats: result.data.formats || [],
          url: url,
          platform: result.data.platform || detectPlatform(url),
          ...result.data
        };
        
        setVideoInfo(info);
        return { success: true, data: info };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      let errorMsg = '提取视频信息失败';
      
      if (err.name === 'TypeError' && err.message.includes('Invalid URL')) {
        errorMsg = '请输入有效的URL地址';
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 清除视频信息
   */
  const clearVideoInfo = useCallback(() => {
    setVideoInfo(null);
    setError(null);
  }, []);

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 获取可用的视频格式选项
   */
  const getFormatOptions = useCallback(() => {
    if (!videoInfo || !videoInfo.formats) {
      return [];
    }

    // 将格式分成四类
    const videoWithAudio = [];
    const videoWithoutAudio = [];
    const audioOnly = [];
    const others = [];
    
    // 分类所有格式
    videoInfo.formats.forEach(format => {
      // 检查格式是否有必要的信息
      const hasVCodec = format.vcodec && format.vcodec !== 'none';
      const hasACodec = format.acodec && format.acodec !== 'none';
      const isAudioOnly = format.resolution === 'audio only' || 
                         (format.format && format.format.toLowerCase().includes('audio only'));
      
      // 创建格式对象
      const formatObj = {
        // 使用format字段作为格式ID (API返回的格式信息中使用format字段而不是format_id)
        value: format.format || format.format_id,
        label: getFormatLabel(format),
        resolution: format.resolution || 'unknown',
        filesize: format.filesize || 0,
        filesize_approx: format.filesize_approx || 0,
        ext: format.ext || 'mp4',
        quality: format.quality || 0,
        fps: format.fps,
        vcodec: format.vcodec,
        acodec: format.acodec,
        ...format
      };
      
      // 按照新需求重新分类
      if (hasVCodec) {  // 首先判断是否是视频
        if (hasACodec) {
        // 视频（含音频）
        videoWithAudio.push(formatObj);
        } else {
        // 视频（不含音频）
        videoWithoutAudio.push(formatObj);
        }
      } else if (isAudioOnly || (!hasVCodec && hasACodec)) {
        // 纯音频
        audioOnly.push(formatObj);
      } else {
        // 其他
        others.push(formatObj);
      }
    });
    
    // 对每个类别内部进行排序
    const sortByResolution = (a, b) => {
      const aHeight = extractHeight(a.resolution);
      const bHeight = extractHeight(b.resolution);
      return bHeight - aHeight; // 高分辨率在前
    };
    
    const sortByFileSize = (a, b) => {
      const aSize = a.filesize_approx || a.filesize || 0;
      const bSize = b.filesize_approx || b.filesize || 0;
      return bSize - aSize; // 大文件在前
    };
    
    videoWithAudio.sort(sortByResolution);
    videoWithoutAudio.sort(sortByResolution);
    audioOnly.sort(sortByFileSize);
    others.sort(sortByFileSize);
    
    // 组合所有格式，并添加分组标签
    const result = [];
    
    // 视频（含音频）
    if (videoWithAudio.length > 0) {
      result.push({
        value: 'category_video_with_audio',
        label: '视频（含音频）',
        disabled: true,
        isGroupLabel: true
      });
      result.push(...videoWithAudio);
    }
    
    // 视频（不含音频）
    if (videoWithoutAudio.length > 0) {
      result.push({
        value: 'category_video_without_audio',
        label: '视频（不含音频）',
        disabled: true,
        isGroupLabel: true
      });
      result.push(...videoWithoutAudio);
    }
    
    // 纯音频
    if (audioOnly.length > 0) {
      result.push({
        value: 'category_audio_only',
        label: '纯音频',
        disabled: true,
        isGroupLabel: true
      });
      result.push(...audioOnly);
    }
    
    // 其他
    if (others.length > 0) {
      result.push({
        value: 'category_others',
        label: '其他格式',
        disabled: true,
        isGroupLabel: true
      });
      result.push(...others);
    }

    return result;
  }, [videoInfo]);

  /**
   * 获取推荐的下载格式
   */
  const getRecommendedFormat = useCallback(() => {
    const options = getFormatOptions();
    
    if (options.length === 0) return '';
    
    // 过滤掉分组标签
    const validOptions = options.filter(opt => !opt.isGroupLabel);
    if (validOptions.length === 0) return '';
    
    // 首先尝试找到视频（含音频）中的高分辨率视频
    const videoWithAudio = validOptions.filter(opt => 
      opt.vcodec && opt.vcodec !== 'none' && 
      opt.acodec && opt.acodec !== 'none'
    );
    
    if (videoWithAudio.length > 0) {
      // 尝试找到1080p或720p分辨率的视频
      const format1080 = videoWithAudio.find(opt => 
        opt.resolution === '1080x1920' || 
        opt.resolution === '1920x1080' || 
        opt.resolution.includes('1080p')
      );
    if (format1080) return format1080.value;
    
      const format720 = videoWithAudio.find(opt => 
        opt.resolution === '720x1280' || 
        opt.resolution === '1280x720' || 
        opt.resolution.includes('720p')
      );
    if (format720) return format720.value;
    
      // 没有找到特定分辨率，返回第一个（通常是最高分辨率）
      return videoWithAudio[0].value;
    }
    
    // 如果没有视频含音频的格式，尝试找含音频的格式
    const audioFormats = validOptions.filter(opt => 
      opt.acodec && opt.acodec !== 'none'
    );
    
    if (audioFormats.length > 0) {
      return audioFormats[0].value;
    }
    
    // 如果上面都没有，就返回第一个有效选项
    return validOptions[0].value;
  }, [getFormatOptions]);

  /**
   * 格式化文件大小
   */
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }, []);

  /**
   * 格式化持续时间
   */
  const formatDuration = useCallback((seconds) => {
    if (!seconds) return '未知时长';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  /**
   * 格式化观看次数
   */
  const formatViewCount = useCallback((count) => {
    if (!count) return '';
    
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    } else {
      return count.toString();
    }
  }, []);

  return {
    videoInfo,
    isLoading,
    error,
    extractVideoInfo,
    clearVideoInfo,
    clearError,
    getFormatOptions,
    getRecommendedFormat,
    formatFileSize,
    formatDuration,
    formatViewCount
  };
};

/**
 * 检测视频平台
 */
function detectPlatform(url) {
  const hostname = new URL(url).hostname.toLowerCase();
  
  if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
    return 'youtube';
  } else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
    return 'twitter';
  } else if (hostname.includes('bilibili.com')) {
    return 'bilibili';
  } else if (hostname.includes('tiktok.com')) {
    return 'tiktok';
  } else if (hostname.includes('instagram.com')) {
    return 'instagram';
  } else {
    return 'unknown';
  }
}

/**
 * 从分辨率字符串中提取高度
 */
function extractHeight(resolution) {
  if (!resolution || resolution === 'unknown') return 0;
  
  // 处理 "320x568" 格式
  if (resolution.includes('x')) {
    const parts = resolution.split('x');
    return parseInt(parts[1]) || 0;
  }
  
  // 处理 "1080p" 格式
  if (resolution.includes('p')) {
    return parseInt(resolution.replace('p', '')) || 0;
  }
  
  return parseInt(resolution) || 0;
}

/**
 * 生成格式标签
 */
function getFormatLabel(format) {
  // 分辨率/规格名称
  let resolution = 'unknown';
  if (format.resolution === 'audio only' || 
     (format.format && format.format.toLowerCase().includes('audio only'))) {
    resolution = 'audio only';
  } else if (format.resolution) {
    resolution = format.resolution;
  } else if (format.height) {
    resolution = `${format.height}p`;
  }
  
  // 文件扩展名
  const ext = format.ext ? `${format.ext.toUpperCase()}` : '';
  
  // 编码信息
  let codecInfo = '';
  if (format.vcodec && format.vcodec !== 'none') {
    codecInfo = format.vcodec;
  } else if (format.acodec && format.acodec !== 'none') {
    codecInfo = format.acodec;
  }
  
  // 文件大小 - 修改为优先使用 filesize_approx
  let filesize = '未知大小';
  if (format.filesize_approx) {
    filesize = formatFileSize(format.filesize_approx);
  } else if (format.filesize) {
    filesize = formatFileSize(format.filesize);
  }
  
  // 按照新需求格式化为: "分辨率或audio(扩展名)[编码] - 文件大小"
  let label = resolution;
  
  // 添加扩展名
  if (ext) {
    label += ` (${ext})`;
  }
  
  // 添加编码信息
  if (codecInfo) {
    label += ` [${codecInfo}]`;
  }
  
  // 添加文件大小
  label += ` - ${filesize}`;
  
  return label;
}

/**
 * 格式化文件大小（工具函数）
 */
function formatFileSize(bytes) {
  if (!bytes) return '';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}